/**
 * Consume one of KC3's AI Server-Sent Event endpoints.
 *
 * Every AI surface (admin test console, agent assistant, suggested reply,
 * rewrite) speaks the same contract, so the parser lives here once rather than
 * being copied per component:
 *
 *   event: meta   { requestedProvider, requestedModel }   — once, first
 *   event: delta  { text }                                — zero or more
 *   event: done   { content, provider, model, latencyMs } — terminal
 *   event: error  { error }                               — terminal
 *
 * A provider failure arrives as `error`, not a dropped connection or a 5xx:
 * these endpoints treat "the model could not answer" as a result to render,
 * not a server fault.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { apiPostStream } from '../api/client.js'
import { clientApiPostStream } from '../api/client-portal.js'

export interface AiStreamResult {
  content: string
  provider?: string
  model?: string
  latencyMs?: number
}

export interface UseAiStream {
  /** Text accumulated so far. Live during a run, final once it settles. */
  text: string
  running: boolean
  /** Set when the run failed; cleared at the start of the next run. */
  error: string | null
  /** Provider/model that actually answered, from the `done` event. */
  result: AiStreamResult | null
  /**
   * POST `body` to `path` and stream the reply.
   *
   * Resolves with the settled result, or null if the run failed or was
   * stopped before producing anything. Returning it matters: React state set
   * inside `run` is not visible to the caller's closure when the await
   * resolves, so a caller that needs the text to act on it (replace an
   * editor's contents, say) cannot read it from `text`.
   */
  run: (path: string, body: unknown) => Promise<AiStreamResult | null>
  /** Cancel an in-flight run. Text produced so far is kept. */
  stop: () => void
  /** Clear text, error and result without touching an in-flight run. */
  reset: () => void
}

export function useAiStream(): UseAiStream {
  const [text, setText] = useState('')
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<AiStreamResult | null>(null)

  const abortRef = useRef<AbortController | null>(null)

  // Unmounting must cancel the run, not leave the server generating into a
  // socket nobody reads.
  useEffect(() => () => abortRef.current?.abort(), [])

  const stop = useCallback(() => abortRef.current?.abort(), [])

  const reset = useCallback(() => {
    setText('')
    setError(null)
    setResult(null)
  }, [])

  const run = useCallback(async (path: string, body: unknown): Promise<AiStreamResult | null> => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setRunning(true)
    setText('')
    setError(null)
    setResult(null)

    let accumulated = ''
    let settled = false
    let finalResult: AiStreamResult | null = null

    try {
      const poster = path.startsWith('/client-portal/') ? clientApiPostStream : apiPostStream
      const res = await poster(path, body, controller.signal)
      if (!res.body) throw new Error('Streaming is not supported by this connection')

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        // SSE frames are separated by a blank line. The trailing piece is
        // usually a partial frame, so it carries over to the next read —
        // without this, tokens split across a network boundary are lost.
        const frames = buffer.split('\n\n')
        buffer = frames.pop() ?? ''

        for (const frame of frames) {
          let event = 'message'
          let data = ''
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) event = line.slice(6).trim()
            else if (line.startsWith('data:')) data += line.slice(5).trim()
          }
          if (!data) continue
          const payload = JSON.parse(data) as Record<string, unknown>

          if (event === 'delta') {
            accumulated += String(payload.text ?? '')
            setText(accumulated)
          } else if (event === 'done') {
            const content = String(payload.content ?? accumulated)
            setText(content)
            finalResult = {
              content,
              ...(payload.provider ? { provider: String(payload.provider) } : {}),
              ...(payload.model ? { model: String(payload.model) } : {}),
              ...(typeof payload.latencyMs === 'number' ? { latencyMs: payload.latencyMs } : {}),
            }
            setResult(finalResult)
            settled = true
          } else if (event === 'error') {
            setError(String(payload.error ?? 'The model could not answer.'))
            settled = true
          }
        }
      }

      // Stream closed with no terminal frame — a proxy timeout or a restart.
      // Keep whatever arrived rather than discarding a partial answer.
      if (!settled) {
        if (accumulated) {
          finalResult = { content: accumulated }
          setResult(finalResult)
        } else {
          setError('The connection closed before the model replied.')
        }
      }
    } catch (err) {
      // Stopping is deliberate, so partial text stands as the result.
      if (controller.signal.aborted) {
        if (accumulated) {
          finalResult = { content: accumulated }
          setResult(finalResult)
        }
      } else {
        setError(err instanceof Error ? err.message : String(err))
      }
    } finally {
      setRunning(false)
      abortRef.current = null
    }

    return finalResult
  }, [])

  return { text, running, error, result, run, stop, reset }
}
